import { Card, CardHeader, CardContent } from "~/atoms/card";
import { isOfficeVersion, type DocumentDetailsVersion } from "~/api/types";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "./NoResult";
import OfficeFrame, { EmptyDocument } from "~/molecules/OfficeFrame";
import { useOfficeData } from "~/api/swr/useOfficeData";
import { usePreviewUrl } from "~/api/swr/usePreviewUrl";
import { Button } from "~/atoms/button";
import DocumentIcon from "./DocumentIcon";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Download, Upload, Languages } from "lucide-react";
import { MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/atoms/dropdown-menu";
import { toast } from "react-toastify";
import { downloadDocument, uploadDocumentFile } from "~/api/v2/requests";
import { UploadFileModal } from "~/organisms/UploadFileModal";
import { useState } from "react";
import {
  OfficeDesktopButton,
  officeFileTypeToTitle,
} from "./OfficeDesktopButton";
import { customer } from "~/api/config";
import { TranslateDocumentModal } from "~/molecules/TranslateDocumentModal";
import { usePostHog } from "posthog-js/react";
import { useUserContext } from "~/hooks/useUserContext";
import { DocumentVersionStatus, PlatformRole } from "~/config/types";
import { featureFlags } from "~/config/featureFlags";
import { printFullName } from "~/helpers/user";
import { useOfficeOnlineUrl } from "~/api/swr/useOfficeOnlineUrl";
import { useSpeEditPermissions } from "~/hooks/useEditPermissions";

interface Props {
  onUpdate: () => void;
  version: DocumentDetailsVersion;
}

const isFondo = customer === "fondo";

export const DocumentPreview = ({ onUpdate, version }: Props) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { user } = useUserContext();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);

  const isDraft = version.status === DocumentVersionStatus.draft;
  const canManage = version.permissions.canManage;
  const { isSpeUserWithoutSso, canNotEditSpeDraftFile } =
    useSpeEditPermissions(version);

  const isPdf = version.fileType === "pdf";

  const canUploadFile = canManage && isDraft;

  const isTranslationAvailable = () => {
    const featureFlag = posthog.isFeatureEnabled(featureFlags.translations);
    if (!featureFlag) return false;
    if (user.role === PlatformRole.reader) return false;
    if (version.status !== DocumentVersionStatus.published) return false;

    return (
      version.fileType === "docx" ||
      version.fileType === "xlsx" ||
      version.fileType === "pptx"
    );
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadDocument(version.id);
      await downloadFile(blob, `${version.title}.${version.fileType}`);
      toast.success(t(($) => $.toasts.success.documentDownloaded));
    } catch {
      toast.error(t(($) => $.toasts.error.documentDownloaded));
    }
  };

  const downloadFile = async (content: Blob, name: string): Promise<void> => {
    try {
      const href = URL.createObjectURL(content);
      const link = window.document.createElement("a");
      link.href = href;
      link.setAttribute("download", name);
      window.document.body.appendChild(link);
      link.click();

      window.document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error("Error downloading document");
    }
  };

  const handleUpload = async (file: File) => {
    try {
      await uploadDocumentFile(version.id, file);
      toast.success(t(($) => $.toasts.success.documentUpdated));
      onUpdate();
    } catch (error) {
      toast.error(t(($) => $.toasts.error.generic));
      throw error;
    }
  };

  return (
    <Card className="flex flex-col gap-0">
      <CardHeader className="flex flex-row items-center justify-between border-b px-6">
        <span className="text-foreground text-base font-medium">
          {t(($) => $.commons.workflow.preview)}
        </span>
        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            {version.lockedBy?.userId === user.id && (
              <span className="text-muted-foreground text-xs">
                {t(($) => $.commons.workflow.documentAlreadyOpenedByYou)}
              </span>
            )}

            {version.lockedBy && version.lockedBy.userId !== user.id && (
              <span className="text-muted-foreground text-xs">
                {t(($) => $.commons.workflow.documentAlreadyOpen, {
                  name: printFullName(version.lockedBy),
                })}
              </span>
            )}
            <PrimaryAction version={version} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <SecondaryAction version={version} />
              {canUploadFile && (
                <DropdownMenuItem
                  onClick={() => setIsUploadModalOpen(true)}
                  disabled={isSpeUserWithoutSso || canNotEditSpeDraftFile}
                >
                  <Upload /> {t(($) => $.commons.workflow.uploadFile)}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleDownload}>
                <Download />
                {t(($) => $.commons.buttons.downloadFile)}
              </DropdownMenuItem>
              {isTranslationAvailable() && (
                <DropdownMenuItem onClick={() => setIsTranslateModalOpen(true)}>
                  <Languages />{" "}
                  {t(($) => $.commons.translation.downloadTranslatedFile)}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="h-[90vh] p-0">
        {isPdf ? (
          <PdfViewer documentVersionId={version.id} />
        ) : (
          <OfficeViewer documentVersionId={version.id} />
        )}
      </CardContent>
      <UploadFileModal
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        versionTitle={version.title}
        currentFileType={version.fileType}
        onUpload={handleUpload}
      />
      <TranslateDocumentModal
        open={isTranslateModalOpen}
        onOpenChange={setIsTranslateModalOpen}
        version={version}
      />
    </Card>
  );
};

const OfficeViewer = ({ documentVersionId }: { documentVersionId: number }) => {
  const posthog = usePostHog();

  const disableWordOnline = posthog.isFeatureEnabled(
    featureFlags.disableWordOnline,
  );

  const {
    previewUrl,
    isLoading: isPreviewLoading,
    error: previewError,
  } = usePreviewUrl(documentVersionId);

  const { data: wopiData, isLoading: isWopiLoading } = useOfficeData(
    // Only fetch WOPI data if preview URL is not available
    previewUrl === null && !isPreviewLoading ? documentVersionId : undefined,
    { embed: true },
  );

  if (disableWordOnline) {
    return null;
  }

  // Show loading while checking for SPE preview
  if (isPreviewLoading) {
    return <CenterSpinner />;
  }

  // If SPE preview URL is available, use it
  if (previewUrl) {
    return <SpePreviewFrame previewUrl={previewUrl} />;
  }

  // Fall back to WOPI preview
  if (isWopiLoading) {
    return <CenterSpinner />;
  }

  if (wopiData === undefined || previewError) {
    return <NoResult />;
  }

  if (wopiData.size === 0) {
    return <EmptyDocument />;
  }

  return <OfficeFrame key={documentVersionId} officeData={wopiData} embed />;
};

const SpePreviewFrame = ({ previewUrl }: { previewUrl: string }) => {
  return (
    <iframe
      src={previewUrl}
      className="size-full border-none"
      title="Document Preview"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};

const PdfViewer = ({ documentVersionId }: { documentVersionId: number }) => {
  return (
    <embed
      src={`/api/v2/document-versions/${documentVersionId}/download/`}
      className="size-full border-none"
      title="PDF Viewer"
    />
  );
};

const PrimaryAction = ({ version }: { version: DocumentDetailsVersion }) => {
  const { t } = useTranslation();
  const { officeOnlineUrl } = useOfficeOnlineUrl(version.id);

  const isOffice = isOfficeVersion(version);

  if (isOffice) {
    return isFondo ? (
      <Button variant="outline" asChild>
        <Link
          to={officeOnlineUrl ?? `/versions/${version.id}/editor`}
          target="_blank"
        >
          <DocumentIcon type={version.fileType} variant="product" />
          {t(($) => $.commons.workflow.openInOfficeForWeb, {
            officeType: officeFileTypeToTitle[version.fileType],
          })}
        </Link>
      </Button>
    ) : (
      <OfficeDesktopButton version={version} />
    );
  }

  return null;
};

const SecondaryAction = ({ version }: { version: DocumentDetailsVersion }) => {
  const { t } = useTranslation();
  const { officeOnlineUrl } = useOfficeOnlineUrl(version.id);
  const { isSpeUserWithoutSso, canNotEditSpeDraftFile } =
    useSpeEditPermissions(version);
  const posthog = usePostHog();

  const disableWordOnline = posthog.isFeatureEnabled(
    featureFlags.disableWordOnline,
  );

  const isOffice = isOfficeVersion(version);

  if (!isOffice) {
    return null;
  }

  if (isFondo) {
    return (
      <DropdownMenuItem asChild>
        <OfficeDesktopButton version={version} />
      </DropdownMenuItem>
    );
  }

  if (disableWordOnline) {
    return null;
  }

  return (
    <DropdownMenuItem
      disabled={isSpeUserWithoutSso || canNotEditSpeDraftFile}
      asChild
    >
      <Link
        to={officeOnlineUrl ?? `/versions/${version.id}/editor`}
        target="_blank"
      >
        <DocumentIcon type={version.fileType} variant="product" />
        {t(($) => $.commons.workflow.openInOfficeForWeb, {
          officeType: officeFileTypeToTitle[version.fileType],
        })}
      </Link>
    </DropdownMenuItem>
  );
};
